import * as THREE from 'three';
import React from 'react';
import EffectorBase, { EffectorPropertyDrawer, LoopableEffector, ReversibleEffector } from "./EffectorBase";
import { CustomEventTypes, EasingFunctionTypes, PropertyTypes, SaveModificationTypes } from "../../../globals";
import Vector3Property from "../../../components/properties/Vector3Property";
import NumberProperty from "../../../components/properties/NumberProperty";
import Engine from '../../Engine';
import ProjectSaver from '../../ProjectSaver';
import MathUtils from '../../utilities/MathUtils';
import TransformUtils from '../../utilities/TransformUtils';
import { TimelineFragment } from '../TimelineFragment';
import { areArraysEqual } from '../../utilities/Misc';

export interface EffectorTransformRotatePivotSaveData
{
	axis: { x: number, y: number, z: number };
	angle: number;
	easing: EasingFunctionTypes;
	rotationPivot: { x: number, y: number, z: number };
	reversed: boolean;
	loopCount: number;
}

export default class EffectorTransformRotatePivot extends EffectorBase<EffectorTransformRotatePivot> implements LoopableEffector, ReversibleEffector
{
	axis: THREE.Vector3;
	angle: number;
	easing: EasingFunctionTypes;
	rotationPivot: THREE.Vector3;
	deltaAngle: number;
	deltaAxis: THREE.Vector3;
	deltaPivot: THREE.Vector3;
	reversed: boolean;
	loopCount: number;
	defaultValues: { axis: { x: number, y: number, z: number }, angle: number, rotationPivot: { x: number, y: number, z: number } };

	constructor(fragment : TimelineFragment)
	{
		super(fragment);

		this.axis = new THREE.Vector3(0, 0, 0);
		this.angle = 0.0;
		this.easing = EasingFunctionTypes.Linear;
		this.rotationPivot = new THREE.Vector3(0, 0, 0);
		this.deltaAngle = 0.0;
		this.deltaAxis = new THREE.Vector3();
		this.deltaPivot = new THREE.Vector3();
		this.reversed = false;
		this.loopCount = 1;
		this.defaultValues = {
			axis: { x: 0, y: 0, z: 0 },
			angle: 0.0,
			rotationPivot: { x: 0, y: 0, z: 0 },
		};
	}

	save = () : EffectorTransformRotatePivotSaveData =>
	{
		const output = {
			axis: { x: this.axis.x, y: this.axis.y, z: this.axis.z },
			angle: this.angle,
			easing: this.easing,
			rotationPivot: { x: this.rotationPivot.x, y: this.rotationPivot.y, z: this.rotationPivot.z },
			reversed: this.reversed,
			loopCount: this.loopCount,
		};

		return output;
	};

	load = (savedData: EffectorTransformRotatePivotSaveData) =>
	{
		this.axis.set(savedData.axis.x, savedData.axis.y, savedData.axis.z);
		this.angle = savedData.angle;
		this.rotationPivot.set(savedData.rotationPivot.x, savedData.rotationPivot.y, savedData.rotationPivot.z);
		this.easing = savedData.easing;
		if (savedData.reversed !== undefined) this.reversed = savedData.reversed;
		if (savedData.loopCount !== undefined) this.loopCount = savedData.loopCount;
	};

	copyValues = (sourceEffector: EffectorTransformRotatePivot) =>
	{
		this.angle = sourceEffector.angle;
		this.axis.copy(sourceEffector.axis);
		this.rotationPivot.copy(sourceEffector.rotationPivot);
		this.easing = sourceEffector.easing;
		this.reversed = sourceEffector.reversed;
		this.loopCount = sourceEffector.loopCount;
	}

	setAngle = (value: number) =>
	{
		this.angle = value;
	}

	setAxis = (x: number, y: number, z: number) =>
	{
		if (typeof (x) === "number" && !isNaN(x)) this.axis.x = x;
		if (typeof (y) === "number" && !isNaN(y)) this.axis.y = y;
		if (typeof (z) === "number" && !isNaN(z)) this.axis.z = z;
	}

	setRotationPivot = (x: number, y: number, z: number) =>
	{
		if (typeof (x) === "number" && !isNaN(x)) this.rotationPivot.x = x;
		if (typeof (y) === "number" && !isNaN(y)) this.rotationPivot.y = y;
		if (typeof (z) === "number" && !isNaN(z)) this.rotationPivot.z = z;
	}

	resetState = () =>
	{
		if (!this.fragment.previousTime || !this.angle || !this.axis.lengthSq()) return;

		this.forEachTargetObject((targetObject) =>
		{
			TransformUtils.rotateAround(targetObject, this.deltaPivot, this.deltaAxis, -this.deltaAngle);
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectPositionChanged);
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectRotationChanged);
		}
	}

	update = (fragmentTime: number ) =>
	{
		if (!fragmentTime || !this.angle || !this.axis.lengthSq()) return;

		this.deltaAngle = MathUtils.round((this.angle * fragmentTime / 180.0 * Math.PI));
		this.deltaAxis.copy(this.axis).normalize();
		this.deltaPivot.copy(this.rotationPivot);

		this.forEachTargetObject((targetObject) =>
		{
			TransformUtils.rotateAround(targetObject, this.deltaPivot, this.deltaAxis, this.deltaAngle);
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectPositionChanged);
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectRotationChanged);
		}
	}

	onAdded = () =>
	{
		for (const object of this.targetObjects())
		{
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Position);
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Rotation);
		}
	}

	getEditorProperties = () =>
	{
		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Angle",
				PropertyTypes.Number,
				<NumberProperty
					value={this.angle}
					step={45}
					defaultValue={this.defaultValues.angle}
					decimals={1}
				/>,
				() => {return this.angle;},
				(v) => {this.setAngle(Number(v));},
			),
			new EffectorPropertyDrawer(
				"Axis (World)",
				PropertyTypes.Vector3,
				<Vector3Property
					value={[this.axis.x, this.axis.y, this.axis.z]}
					renderChildrenInSeparateRows = {true}
					defaultValue={this.defaultValues.axis}
				/>,
				() => {return [this.axis.x, this.axis.y, this.axis.z];},
				(v) => {this.setAxis(v.x, v.y, v.z);},
				(a, b) => {return areArraysEqual(a, b);},
			),
			new EffectorPropertyDrawer(
				"Pivot",
				PropertyTypes.Vector3,
				<Vector3Property
					value={[this.rotationPivot.x, this.rotationPivot.y, this.rotationPivot.z]}
					step={0.1}
					renderChildrenInSeparateRows = {true}
					defaultValue={this.defaultValues.rotationPivot}
					decimals={3}
				/>,
				() => {return [this.rotationPivot.x, this.rotationPivot.y, this.rotationPivot.z];},
				(v) => {this.setRotationPivot(v.x, v.y, v.z);},
				(a, b) => {return areArraysEqual(a, b);},
			),
		];
	}
}
